<template>
  <div class="login-page">
    <div class="login-shell">
      <section class="brand-panel">
        <div class="brand-badge">FortunaPlan Admin</div>
        <h1>广告管理系统</h1>
        <p>
          保持现有后台使用习惯的同时，重建更清晰的登录体验、权限模型和管理流程。
        </p>
        <ul class="brand-points">
          <li>统一管理员登录态</li>
          <li>动态菜单与页面权限</li>
          <li>更稳定的后台访问控制</li>
        </ul>
      </section>

      <section class="login-card">
        <div class="card-header">
          <h2>管理员登录</h2>
          <span>请输入账号密码进入后台</span>
        </div>

        <el-form
          ref="loginForm"
          :model="form"
          :rules="rules"
          label-position="top"
          @submit.native.prevent
        >
          <el-form-item label="账号" prop="username">
            <el-input
              v-model.trim="form.username"
              placeholder="请输入管理员账号"
              prefix-icon="el-icon-user"
              @keyup.enter.native="loginClick"
            />
          </el-form-item>

          <el-form-item label="密码" prop="password">
            <el-input
              v-model="form.password"
              type="password"
              show-password
              placeholder="请输入登录密码"
              prefix-icon="el-icon-lock"
              @keyup.enter.native="loginClick"
            />
          </el-form-item>

          <el-button
            class="login-btn"
            type="primary"
            :loading="loading"
            @click="loginClick"
          >
            {{ loading ? "登录中..." : "进入后台" }}
          </el-button>
        </el-form>
      </section>
    </div>
  </div>
</template>

<script>
import { registerDynamicRoutes, resolveDefaultPath } from "@/router";

export default {
  data() {
    return {
      loading: false,
      form: {
        username: "",
        password: "",
      },
      rules: {
        username: [
          { required: true, message: "请输入管理员账号", trigger: "blur" },
        ],
        password: [
          { required: true, message: "请输入登录密码", trigger: "blur" },
        ],
      },
    };
  },
  methods: {
    async loginClick() {
      const valid = await new Promise((resolve) => {
        this.$refs.loginForm.validate((result) => resolve(result));
      });

      if (!valid) {
        return;
      }

      this.loading = true;
      try {
        const data = await this.$api.getLogin.login({ ...this.form });
        if (data.code !== 200) {
          return;
        }

        const payload = data.data || {};
        const menuTree = payload.menu_tree || [];

        this.$store.dispatch("loginSuccess", {
          token: payload.token,
          adminInfo: payload.admin_info || {},
          menuTree,
          permissionKeys: payload.permission_keys || [],
          defaultPath: payload.default_path,
        });

        const routes = registerDynamicRoutes(menuTree);
        this.$store.commit("setRouteReady", true);

        if (!routes.length) {
          this.$message.error("暂无可用菜单权限");
          this.$store.dispatch("logout");
          return;
        }

        const redirect = this.$route.query.redirect;
        const targetPath = redirect || resolveDefaultPath(menuTree, payload.default_path);
        this.$router.replace(targetPath);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.login-page {
  min-height: 100vh;
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top left, rgba(83, 147, 255, 0.28), transparent 32%),
    radial-gradient(circle at bottom right, rgba(37, 87, 188, 0.32), transparent 30%),
    linear-gradient(135deg, #0f3f87 0%, #1756b6 42%, #4d8ff1 100%);
}

.login-shell {
  width: 100%;
  max-width: 1120px;
  min-height: 620px;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  border-radius: 28px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 70px rgba(9, 34, 81, 0.28);
  backdrop-filter: blur(18px);
}

.brand-panel {
  position: relative;
  padding: 72px 64px;
  color: #fff;
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04)),
    linear-gradient(180deg, rgba(10, 35, 82, 0.35), rgba(10, 35, 82, 0.05));

  &::after {
    content: "";
    position: absolute;
    right: -120px;
    bottom: -120px;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    filter: blur(10px);
  }

  h1 {
    margin: 22px 0 18px;
    font-size: 44px;
    line-height: 1.12;
    letter-spacing: 1px;
  }

  p {
    max-width: 500px;
    margin: 0;
    font-size: 16px;
    line-height: 1.85;
    color: rgba(255, 255, 255, 0.84);
  }
}

.brand-badge {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-size: 12px;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}

.brand-points {
  margin: 36px 0 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 18px;
    padding-left: 18px;
    color: rgba(255, 255, 255, 0.92);
    font-size: 15px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 9px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #b9d6ff;
      box-shadow: 0 0 0 6px rgba(185, 214, 255, 0.12);
    }
  }
}

.login-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 48px;
  background: rgba(248, 251, 255, 0.96);
}

.card-header {
  margin-bottom: 28px;

  h2 {
    margin: 0 0 8px;
    color: #173b7a;
    font-size: 30px;
  }

  span {
    color: #7390bc;
    font-size: 14px;
  }
}

.login-btn {
  width: 100%;
  height: 46px;
  margin-top: 10px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #2f73eb, #164db1);
  box-shadow: 0 12px 24px rgba(47, 115, 235, 0.22);
}

/deep/ .el-form-item__label {
  padding-bottom: 8px;
  color: #3d5f93;
  font-weight: 600;
}

/deep/ .el-input__inner {
  height: 46px;
  border-radius: 14px;
  border-color: #d8e3f3;
  background: #f8fbff;

  &:focus {
    border-color: #2f73eb;
    box-shadow: 0 0 0 4px rgba(47, 115, 235, 0.08);
  }
}

/deep/ .el-input__prefix {
  left: 12px;
}

@media (max-width: 980px) {
  .login-page {
    padding: 18px;
  }

  .login-shell {
    grid-template-columns: 1fr;
  }

  .brand-panel {
    padding: 36px 28px 18px;

    h1 {
      font-size: 30px;
    }
  }

  .login-card {
    padding: 30px 24px 36px;
  }
}
</style>
